/**
 * PurchaseOptionsEditor — the "Related Products" editor: a compact comparison
 * table of the ways to buy one inventory item (public.purchase_options):
 * vendor, vendor item name, SKU, pack, prices, the comparable price in the
 * relationship's comparison unit, price age, status flags, row actions,
 * manual additions, per-option price history, and the manual comparison
 * review (pick the unit, state each package's quantity, mark it verified).
 *
 * Row actions also cover grouping: "Move…" re-points an option at another
 * item (attachOptionToItem, inline item search). Removing an option from its
 * current related-product group stays in the modal's single, dedicated
 * "Disconnect into its own item" control instead of being duplicated here.
 *
 * Mounted from the InventoryManager item modal and the CountInterface
 * multi-vendor modal. The item's count unit is authoritative:
 * qty_per_package is expressed in it and normalized_unit_cost is displayed
 * per count unit. The Comparable column renders each option's stored
 * relationship comparison (utils/priceComparison — the same view-model every
 * surface uses): one practical unit for every row, the lowest tag, and the
 * Verified / Calculated / AI estimate / Needs review provenance. No unit
 * math happens here.
 *
 * Vendor identity resolves through vendor_id → store vendors (display_name)
 * first; when vendor_id is null (resolution failed at import) the raw
 * vendor_name_text read from the source document is shown instead, tagged
 * 'unlinked'. vendor_description is the vendor's own product/item name and
 * appears in its own column — never as the vendor, and never a raw uuid.
 *
 * Props:
 *   storeId         - current store id
 *   inventoryItemId - the item whose options are being managed
 *   countUnit       - item.unit (e.g. 'gal', 'each')
 *   item?           - the full inventory item object; when the options load
 *                     comes back EMPTY, the item's legacy pricing columns are
 *                     mirrored into one heal option (see mirrorFieldsFromItem)
 *   store?          - the store row, for the configured costing method
 *   showSummary?    - render the ItemPurchaseSummary header above the table
 *                     (default true). The header is what keeps the four money
 *                     concepts — current inventory cost, preferred vendor,
 *                     last purchased, best comparable price — from collapsing
 *                     into one ambiguous number.
 *   onChanged?      - called after every successful mutation (list already
 *                     refreshed) so hosts can reload derived data
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import supabase from '../config/supabase';
import {
  fetchOptionsForItems,
  makePreferred,
  reactivate,
  upsertManualOption,
  fetchOptionPriceHistory,
} from '../services/purchaseOptionService';
import { attachOptionToItem } from '../services/itemCrossReferenceService';
import {
  setComparisonOverride,
  clearComparisonOverride,
  comparisonFamilyOf,
  MANUAL_COMPARISON_UNITS,
} from '../services/comparisonOverrideService';
import { formatOptionLabel, optionSummaryLine } from '../utils/purchaseOptions';
// One reader of what a pack says one package IS, shared with the import
// planner and mirrored (narrowly) by the legacy-mirror SQL trigger.
import { statedOrderUnitFromPack } from '../../../supabase/functions/_shared/packText.mjs';
import { buildComparablePriceDisplay } from '../utils/priceComparison';
import { buildItemCostSummary, STALE_PRICE_DAYS } from '../utils/itemCostSummary';
import ItemPurchaseSummary, { optionBadges } from './ItemPurchaseSummary';
import OrderingRulesEditor from './ordering/OrderingRulesEditor';
import { interpretPurchasePackage, confirmPurchasePackage } from '../services/purchaseUnitsService';

const VERIFICATION_BADGES = {
  verified: { bg: '#E8F5E9', fg: '#2E7D32', label: 'verified' },
  unverified: { bg: '#F5F1EC', fg: '#8B7B73', label: 'unverified' },
  rejected: { bg: '#FDECEA', fg: '#b71c1c', label: 'rejected' },
};

const money = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? `$${parsed.toFixed(2)}` : '—';
};

const formatDate = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString();
};

// Whole days since the price went effective (or the row was last updated).
// Returns null when there is no usable date at all.
const priceAgeDays = (option) => {
  const value = option?.effective_date ?? option?.updated_at;
  if (!value) return null;
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return null;
  return Math.max(0, Math.floor((Date.now() - time) / 86400000));
};

const badgeStyle = (badge) => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 999,
  background: badge.bg,
  color: badge.fg,
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  whiteSpace: 'nowrap',
});

const styles = {
  wrap: { fontFamily: 'Open Sans, sans-serif', color: '#3D2518', fontSize: 13 },
  muted: { color: '#8B7B73', fontSize: 12 },
  summary: { color: '#8B7B73', fontSize: 12, marginBottom: 10 },
  tableWrap: {
    overflowX: 'auto', marginBottom: 10,
    background: '#fff', border: '1.5px solid #DCCFC2', borderRadius: 10,
  },
  table: { width: '100%', minWidth: 980, borderCollapse: 'collapse', fontSize: 12 },
  th: {
    textAlign: 'left', padding: '6px 8px', background: '#FBF8F5',
    color: '#5A4A42', fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.05em', borderBottom: '1.5px solid #E9DFD6', whiteSpace: 'nowrap',
  },
  td: { padding: '7px 8px', borderBottom: '1px solid #F1EAE2', verticalAlign: 'top' },
  row: (inactive) => (inactive ? { background: '#FAF7F4', opacity: 0.65 } : {}),
  vendor: { fontWeight: 700, color: '#3D2518', whiteSpace: 'nowrap' },
  star: { color: '#E6A817', marginLeft: 4 },
  noVendor: { color: '#C9B8AC', fontStyle: 'italic' },
  unlinkedTag: {
    display: 'inline-block', marginLeft: 4, padding: '0 5px', borderRadius: 999,
    background: '#F5F0EB', color: '#A89890', fontSize: 9, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
  },
  sub: { display: 'block', color: '#8B7B73', fontSize: 11, marginTop: 2 },
  badges: { display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' },
  lowestTag: {
    display: 'inline-block', marginLeft: 6, padding: '1px 6px', borderRadius: 999,
    background: '#E8F5E9', color: '#2E7D32', fontSize: 10, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
  },
  ageStale: { color: '#B26A00', fontWeight: 700 },
  actions: { display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start', minWidth: 36 },
  smallBtn: {
    display: 'inline-flex', alignItems: 'center', minHeight: 24, padding: '2px 8px',
    borderRadius: 7, fontWeight: 600, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
    background: '#fff', color: '#5A4A42', border: '1.5px solid #d5c9c0', whiteSpace: 'nowrap',
  },
  moreBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 30,
    minHeight: 24, padding: '0 8px', borderRadius: 7, fontWeight: 800, fontSize: 16,
    lineHeight: 1, cursor: 'pointer', fontFamily: 'inherit', background: '#fff',
    color: '#5A4A42', border: '1.5px solid #d5c9c0',
  },
  actionMenu: {
    flexBasis: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch',
    gap: 4, minWidth: 126, paddingTop: 2,
  },
  actionMenuBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%',
    minHeight: 26, padding: '3px 8px', borderRadius: 7, fontWeight: 600, fontSize: 11,
    cursor: 'pointer', fontFamily: 'inherit', background: '#fff', color: '#5A4A42',
    border: '1.5px solid #d5c9c0', whiteSpace: 'nowrap',
  },
  amberBtn: {
    display: 'inline-flex', alignItems: 'center', minHeight: 24, padding: '2px 8px',
    borderRadius: 7, fontWeight: 700, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
    background: '#FFF4E0', color: '#8A5A00', border: '1.5px solid #E6B866', whiteSpace: 'nowrap',
  },
  error: {
    padding: '8px 12px', color: '#b71c1c', background: '#FDECEA', borderRadius: 8,
    border: '1px solid #f5c2bd', fontSize: 12, marginBottom: 10,
  },
  addPanel: {
    background: '#FBF8F5', border: '1.5px dashed #DCCFC2', borderRadius: 10,
    padding: '10px 12px', marginTop: 4,
  },
  movePanel: {
    background: '#FBF8F5', border: '1.5px dashed #DCCFC2', borderRadius: 10,
    padding: '10px 12px',
  },
  moveTargetBtn: {
    display: 'block', width: '100%', textAlign: 'left', marginTop: 4,
    padding: '6px 9px', borderRadius: 8, fontSize: 12, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
    background: '#fff', color: '#3D2518', border: '1.5px solid #E9DFD6',
  },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginTop: 8 },
  field: { display: 'flex', flexDirection: 'column' },
  fieldLabel: { fontSize: 10, fontWeight: 700, color: '#5A4A42', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 },
  input: {
    padding: '7px 9px', border: '1.5px solid #E9DFD6', borderRadius: 8,
    fontSize: 13, color: '#3D2518', background: '#fff', fontFamily: 'inherit',
  },
  primaryBtn: {
    display: 'inline-flex', alignItems: 'center', minHeight: 34, padding: '7px 16px',
    borderRadius: 9, fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
    background: '#5A4A42', color: '#fff', border: 'none',
  },
  historyBox: {
    background: '#FBF8F5', border: '1px solid #EDE4DA', borderRadius: 8,
    padding: '8px 10px', fontSize: 12, color: '#5A4A42',
  },
  historyRow: { padding: '3px 0', borderBottom: '1px solid #F1EAE2' },
  analysisPanel: { fontSize: 12, color: '#3E2723', lineHeight: 1.45 },
  analysisGrid: {
    display: 'grid',
    gridTemplateColumns: 'max-content minmax(0, 1fr)',
    columnGap: 12,
    rowGap: 3,
    alignItems: 'baseline',
  },
  analysisLabel: {
    fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em',
    color: '#8B7B73', whiteSpace: 'nowrap',
  },
  analysisWarn: {
    marginTop: 8, fontSize: 11.5, color: '#8A5A00', background: '#FFF4E0',
    border: '1px solid #E6B866', borderRadius: 8, padding: '7px 9px', lineHeight: 1.4,
  },
  snapshot: {
    background: '#FFF9EC', border: '1px solid #E6B866', borderRadius: 8,
    padding: '8px 10px', fontSize: 11, color: '#5A4A42', whiteSpace: 'pre-wrap',
    overflowX: 'auto', fontFamily: 'monospace',
  },
};

// Vendor · vendor item · SKU · pack · qty · package $ · unit cost ·
// comparable · age · status · actions.
const COLUMN_COUNT = 11;

// Move-to-item search: cap the result list, fetching one extra so the current
// item can be filtered out without dropping below the cap.
const MOVE_RESULT_LIMIT = 8;

const EMPTY_FORM = {
  vendorId: '',
  vendorSku: '',
  packDescription: '',
  purchaseUnit: '',
  qtyPerPackage: '',
  packagePrice: '',
  moqPackages: '',
  orderIncrement: '',
};

const firstPositive = (...values) => {
  for (const value of values) {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
};

// Mirror of the phase-1 legacy backfill (see
// supabase/migrations/20260724120002_backfill_purchase_options.sql): when an
// active item somehow has NO purchase options at all, recreate its single
// mirror option from the legacy pricing columns so "0 active purchase
// options" stays impossible. Returns null when there is nothing worth
// mirroring (no vendor and no positive price).
const mirrorFieldsFromItem = (source) => {
  if (!source) return null;
  const casePrice = Number.parseFloat(source.case_price);
  const caseQty = Number.parseFloat(source.case_quantity);
  const hasCasePrice = Number.isFinite(casePrice) && casePrice > 0;
  const price = firstPositive(source.case_price, source.unit_price);
  const hasVendor = (source.vendor_id != null && source.vendor_id !== '')
    || (typeof source.vendor === 'string' && source.vendor.trim() !== '');
  if (price === null && !hasVendor) return null;
  return {
    vendor_id: source.vendor_id ?? null,
    // `case_price` is the price of ONE stated package, not proof the package
    // is a case. When the pack names the package ("750 mL bottle"), it names
    // the order unit — and purchase_unit is arithmetic, not a label: a bottle
    // called a case is an order the vendor fills by the case.
    purchase_unit: hasCasePrice
      ? (statedOrderUnitFromPack(source.pack_size) || 'case')
      : (source.unit || 'each'),
    pack_description: source.pack_size ?? null,
    qty_per_package: hasCasePrice && Number.isFinite(caseQty) && caseQty > 1 ? caseQty : 1,
    package_price: price,
  };
};

export default function PurchaseOptionsEditor({
  storeId,
  inventoryItemId,
  countUnit,
  item,
  store,
  showSummary = true,
  // A linked Related Products group often stores one vendor option on each
  // member item. An unresolved group tile routes here with these props so
  // the EXISTING manual comparison editor can review that one member option;
  // ordinary standalone one-option items keep the old hidden control.
  allowSingleOptionComparisonReview = false,
  initialComparisonReviewOpen = false,
  comparisonReviewUnit = '',
  onChanged,
}) {
  const [options, setOptions] = useState(null); // null = loading
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Store vendors for vendor-name resolution and the add-form picker
  // (non-fatal when unavailable).
  const [vendors, setVendors] = useState([]);

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');

  // Per-option expanders (only one of each open at a time — rows are small).
  const [historyFor, setHistoryFor] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [conversionFor, setConversionFor] = useState(null);
  const [actionsFor, setActionsFor] = useState(null);
  // "Ordering rules…" — the SHARED editor for one option's MOQ / order
  // increment / fractional flag (same dialog Savings Review and the draft PO
  // review mount; one purchase_options row, one stored copy of the minimum).
  const [orderingRulesFor, setOrderingRulesFor] = useState(null);

  // Package analysis: which option's panel is open, which is in flight, and
  // the interpretation itself keyed by option id. An interpretation is a
  // PROPOSAL until somebody accepts it — nothing here is written on arrival.
  const [analysisFor, setAnalysisFor] = useState(null);
  const [analyzing, setAnalyzing] = useState(null);
  const [analysisByOptionId, setAnalysisByOptionId] = useState({});

  // "Move…" inline item search (group this option with another item).
  const [moveFor, setMoveFor] = useState(null);
  const [moveQuery, setMoveQuery] = useState('');
  const [moveResults, setMoveResults] = useState([]);
  const [moveSearching, setMoveSearching] = useState(false);

  // Manual comparison review: the unit picked, one quantity per active
  // option, and — because the host owns the `item` prop — a local patch of
  // the comparison_* columns a save/clear just wrote, so the table reflects
  // the override without waiting for the host to refetch the item.
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewUnit, setReviewUnit] = useState(MANUAL_COMPARISON_UNITS[0]);
  const [reviewQty, setReviewQty] = useState({});
  const [reviewError, setReviewError] = useState('');
  const [comparisonPatch, setComparisonPatch] = useState(null);

  // Heal runs at most once per (store, item) so a heal that still yields
  // zero options cannot loop.
  const healAttemptedForRef = useRef(null);
  const initialReviewOpenedForRef = useRef(null);

  // The price-history ledger backs both the per-option history panels and the
  // header's "Last purchased" slot, so it is loaded once with the options
  // rather than on first expand. Non-fatal: a failed ledger read leaves the
  // rest of the editor working.
  const loadHistory = useCallback(async () => {
    if (!storeId || !inventoryItemId) return;
    try {
      const rows = await fetchOptionPriceHistory(storeId, inventoryItemId);
      setHistoryRows(rows || []);
    } catch (err) {
      console.warn('[purchase-options] price history unavailable (non-fatal):', err?.message);
    }
  }, [storeId, inventoryItemId]);

  const load = useCallback(async () => {
    if (!storeId || !inventoryItemId) {
      setOptions([]);
      return;
    }
    try {
      const grouped = await fetchOptionsForItems(storeId, [inventoryItemId]);
      const loaded = grouped[inventoryItemId] || [];

      // Runtime safety net: an item with legacy pricing but zero purchase
      // options gets one mirror option created quietly, then the list
      // reloads. Non-fatal — failure just shows the normal empty state.
      if (loaded.length === 0) {
        const mirror = mirrorFieldsFromItem(item);
        const healKey = `${storeId}:${inventoryItemId}`;
        if (mirror && healAttemptedForRef.current !== healKey) {
          healAttemptedForRef.current = healKey;
          try {
            // A reconstruction of legacy pricing, not a real event — it must
            // not stamp operational recency and outrank genuine purchases.
            await upsertManualOption(storeId, inventoryItemId, mirror, { operationalSource: null });
            const regrouped = await fetchOptionsForItems(storeId, [inventoryItemId]);
            setOptions(regrouped[inventoryItemId] || []);
            setError('');
            return;
          } catch (healErr) {
            console.warn('[purchase-options] auto-heal failed (non-fatal):', healErr?.message);
          }
        }
      }

      setOptions(loaded);
      setError('');
    } catch (err) {
      setOptions([]);
      setError(err?.message || 'Failed to load purchase options.');
    }
  }, [storeId, inventoryItemId, item]);

  useEffect(() => {
    setOptions(null);
    load();
    loadHistory();
  }, [load, loadHistory]);

  useEffect(() => {
    if (!storeId) return undefined;
    let cancelled = false;
    Promise.resolve(
      supabase.from('vendors').select('id, display_name').eq('store_id', storeId).order('display_name'),
    )
      .then(({ data, error: vendorError }) => {
        if (!cancelled) setVendors(vendorError ? [] : data || []);
      })
      .catch(() => { if (!cancelled) setVendors([]); });
    return () => { cancelled = true; };
  }, [storeId]);

  // Vendor display names by id. Vendor identity comes ONLY from this map:
  // vendor_description is the vendor's product name (its own column), and a
  // raw uuid is never shown.
  const vendorNameById = useMemo(() => {
    const map = {};
    vendors.forEach((vendor) => { if (vendor?.id != null) map[vendor.id] = vendor.display_name; });
    return map;
  }, [vendors]);

  // Shared vendor-label fallback chain: vendor_id → store vendors
  // (display_name) first, then the raw vendor_name_text from the source
  // document when the option never linked to a vendor. Returns 'Vendor …'
  // while an id has no resolved name yet, and null only when the option has
  // no vendor identity at all.
  const resolveVendorName = (option) =>
    vendorNameById[option.vendor_id]
    || (option.vendor_id ? 'Vendor …' : null)
    || (option.vendor_name_text || null);

  // The stored relationship comparison, rendered through the shared
  // view-model so this table, the count-card badge, and the vendor picker
  // quote identical strings. The local patch overlays comparison_* columns
  // written by a manual review this session.
  const effectiveItem = useMemo(
    () => (comparisonPatch ? { ...(item || {}), ...comparisonPatch } : (item || {})),
    [item, comparisonPatch],
  );
  const comparable = useMemo(() => buildComparablePriceDisplay({
    item: effectiveItem,
    options: options || [],
    vendorNameById,
  }), [effectiveItem, options, vendorNameById]);
  const comparableRowById = useMemo(
    () => new Map(comparable.options.map((row) => [String(row.optionId), row])),
    [comparable],
  );

  // The four separate money concepts for the header, plus the per-row
  // Preferred / Best price / Last purchased badges. Derived from the options
  // already in hand (and the price-history ledger once it has been loaded),
  // so the header costs no extra round trip.
  const costSummary = useMemo(() => buildItemCostSummary({
    item: item || { id: inventoryItemId, unit: countUnit },
    options: options || [],
    priceHistory: historyRows,
    store,
    vendorNameById,
  }), [item, inventoryItemId, countUnit, options, historyRows, store, vendorNameById]);

  const runMutation = async (action) => {
    setBusy(true);
    setError('');
    try {
      await action();
      await load();
      if (typeof onChanged === 'function') onChanged();
      return true;
    } catch (err) {
      setError(err?.message || 'Action failed. Please try again.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleMakePreferred = (option) => runMutation(() =>
    makePreferred(option.id, option.inventory_item_id ?? inventoryItemId));

  const handleReactivate = (option) => runMutation(() => reactivate(option.id));

  const closeMovePanel = () => {
    setMoveFor(null);
    setMoveQuery('');
    setMoveResults([]);
    setMoveSearching(false);
  };

  const openMovePanel = (option) => {
    if (moveFor === option.id) {
      closeMovePanel();
      return;
    }
    setMoveFor(option.id);
    setMoveQuery('');
    setMoveResults([]);
    setMoveSearching(false);
  };

  // Inline item search for the move target: ilike on description within this
  // store, capped at MOVE_RESULT_LIMIT with archived/deleted items and the
  // current item excluded.
  // Non-fatal — failures surface in the existing error banner.
  const searchMoveTargets = async (rawQuery) => {
    const query = rawQuery.trim();
    if (!query || !storeId) {
      setMoveResults([]);
      setMoveSearching(false);
      return;
    }
    setMoveSearching(true);
    try {
      const pattern = `%${query.replace(/[%_\\]/g, '')}%`;
      const { data, error: searchError } = await supabase
        .from('inventory_items')
        .select('id, description, brand, vendor')
        .eq('store_id', storeId)
        .not('is_active', 'is', false)
        .is('deleted_at', null)
        .ilike('description', pattern)
        .order('description')
        .limit(MOVE_RESULT_LIMIT + 1);
      if (searchError) throw new Error(searchError.message);
      setMoveResults(
        (data || [])
          .filter((row) => String(row.id) !== String(inventoryItemId))
          .slice(0, MOVE_RESULT_LIMIT),
      );
      setError('');
    } catch (err) {
      setMoveResults([]);
      setError(err?.message || 'Item search failed.');
    } finally {
      setMoveSearching(false);
    }
  };

  const handleMoveConfirm = async (option, target) => {
    const label = formatOptionLabel({ ...option, vendor_name: resolveVendorName(option), count_unit: countUnit }) || 'this purchase option';
    const targetLabel = target.description || `item ${target.id}`;
    if (!window.confirm(`Move ${label} to "${targetLabel}"? It becomes a way to buy that item.`)) return;
    const ok = await runMutation(() =>
      attachOptionToItem(option.id, target.id, { channel: 'editor' }));
    if (ok) closeMovePanel();
  };

  const toggleHistory = async (option) => {
    if (historyFor === option.id) {
      setHistoryFor(null);
      return;
    }
    setHistoryFor(option.id);
    // Refresh on expand so a price written since the editor opened shows up;
    // the rows already loaded stay on screen meanwhile.
    setHistoryLoading(true);
    try {
      const rows = await fetchOptionPriceHistory(storeId, inventoryItemId);
      setHistoryRows(rows || []);
    } catch (err) {
      setError(err?.message || 'Failed to load price history.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    const qty = Number.parseFloat(form.qtyPerPackage);
    if (!Number.isFinite(qty) || qty <= 0) {
      setFormError('Qty per package must be a positive number.');
      return;
    }
    const price = Number.parseFloat(form.packagePrice);
    if (!Number.isFinite(price) || price <= 0) {
      setFormError('Package price must be a positive number.');
      return;
    }
    const moq = Number.parseInt(form.moqPackages, 10);
    // The legal ordering step, in packages. Fractional values (0.25 on a
    // by-the-pound option) are for true cut-to-order vendors; blank means
    // whole packages. Never inferred from anything else.
    const increment = Number.parseFloat(form.orderIncrement);
    const vendor = vendors.find((v) => String(v.id) === String(form.vendorId));

    setFormError('');
    const saved = await runMutation(async () => {
      await upsertManualOption(storeId, inventoryItemId, {
        vendor_id: form.vendorId || null,
        vendor_description: vendor?.display_name || null,
        vendor_sku: form.vendorSku.trim() || null,
        pack_description: form.packDescription.trim() || null,
        purchase_unit: form.purchaseUnit.trim() || null,
        qty_per_package: qty,
        package_price: price,
        moq_packages: Number.isFinite(moq) && moq > 0 ? moq : 1,
        order_increment: Number.isFinite(increment) && increment > 0 ? increment : null,
      });
    });
    if (saved) {
      setForm(EMPTY_FORM);
      setAddOpen(false);
    }
  };

  // Price-history ledger is item-level; when a row names its purchase option,
  // only surface it under that option. Option-less legacy rows show everywhere.
  const visibleHistoryRows = historyRows.filter(
    (row) => !row.purchase_option_id || String(row.purchase_option_id) === String(historyFor),
  );

  /* ── Manual comparison review ──────────────────────────────────────────── */

  const activeOptionRows = (options || []).filter((option) => option?.status === 'active');
  const hasManualOverride = comparable.comparisonSource === 'manual_override';
  const lockedReviewUnit = MANUAL_COMPARISON_UNITS.includes(comparisonReviewUnit)
    ? comparisonReviewUnit
    : '';

  const openReviewPanel = () => {
    // Seed with what the comparison already resolved, so reviewing is
    // correcting numbers, not retyping them. A linked-group route owns the
    // denominator: this member must be stated in the group's settled unit or
    // its verified quantity would still be dimensionally incompatible.
    const seededUnit = lockedReviewUnit
      || (MANUAL_COMPARISON_UNITS.includes(comparable.comparisonUnit)
      ? comparable.comparisonUnit
      : MANUAL_COMPARISON_UNITS[0]);
    const seededQty = {};
    activeOptionRows.forEach((option) => {
      const row = comparableRowById.get(String(option.id));
      if (row?.quantityPerPurchasePackage != null) {
        seededQty[option.id] = String(row.quantityPerPurchasePackage);
      }
    });
    setReviewUnit(seededUnit);
    setReviewQty(seededQty);
    setReviewError('');
    setReviewOpen(true);
  };

  const toggleReviewPanel = () => {
    if (reviewOpen) {
      setReviewOpen(false);
      return;
    }
    openReviewPanel();
  };

  // "Needs quantity confirmation" is a direct route, not another landing
  // page: once this option list arrives, open the manual review immediately.
  // The ref prevents a save/close rerender from reopening it.
  useEffect(() => {
    if (!initialComparisonReviewOpen || options === null || activeOptionRows.length === 0) return;
    const key = `${storeId}:${inventoryItemId}`;
    if (initialReviewOpenedForRef.current === key) return;
    initialReviewOpenedForRef.current = key;
    openReviewPanel();
    // options is the load boundary; comparable is derived from that same list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialComparisonReviewOpen, options, storeId, inventoryItemId]);

  const handleReviewSave = async () => {
    const perOption = [];
    for (const option of activeOptionRows) {
      const quantity = Number.parseFloat(reviewQty[option.id]);
      if (!Number.isFinite(quantity) || quantity <= 0) {
        setReviewError(`Enter a positive ${reviewUnit} quantity per package for every option.`);
        return;
      }
      perOption.push({ optionId: option.id, quantity });
    }
    setReviewError('');
    const saved = await runMutation(() => setComparisonOverride(
      storeId,
      inventoryItemId,
      {
        unit: reviewUnit,
        family: comparisonFamilyOf(reviewUnit),
        reasoning: 'Package quantities reviewed by hand in the purchase options editor.',
      },
      perOption,
    ));
    if (saved) {
      setComparisonPatch({
        comparison_unit: reviewUnit,
        comparison_unit_family: comparisonFamilyOf(reviewUnit),
        comparison_unit_source: 'manual_override',
        comparison_unit_confidence: 1,
      });
      setReviewOpen(false);
    }
  };

  const handleReviewClear = async () => {
    const cleared = await runMutation(() => clearComparisonOverride(storeId, inventoryItemId));
    if (cleared) {
      setComparisonPatch({
        comparison_unit: null,
        comparison_unit_family: null,
        comparison_unit_source: null,
        comparison_unit_confidence: null,
        comparison_unit_reasoning: null,
        comparison_unit_updated_at: null,
      });
      setReviewOpen(false);
    }
  };

  if (options === null) {
    return <div style={styles.wrap}><div style={styles.muted}>Loading purchase options…</div></div>;
  }

  const renderBadges = (option, inactive) => {
    const verification = VERIFICATION_BADGES[option.verification_status] || VERIFICATION_BADGES.unverified;
    // Role badges first — Preferred / Best price / Last purchased answer the
    // question the row is being scanned for. A row can carry more than one.
    const roles = inactive ? [] : optionBadges(option, costSummary)
      .filter((badge) => badge.key === 'preferred' || badge.key === 'best' || badge.key === 'last');
    return (
      <div style={styles.badges}>
        {roles.map((badge) => (
          <span key={badge.key} style={badgeStyle({ bg: badge.tone.bg, fg: badge.tone.fg })}>
            {badge.label}
          </span>
        ))}
        <span style={badgeStyle(verification)}>{verification.label}</span>
        {option.is_approved && <span style={badgeStyle({ bg: '#E3F2FD', fg: '#0d47a1' })}>approved</span>}
        {option.is_hidden_from_allocation && <span style={badgeStyle({ bg: '#F5F1EC', fg: '#8B7B73' })}>hidden</span>}
        {inactive && <span style={badgeStyle({ bg: '#F5F1EC', fg: '#8B7B73' })}>inactive</span>}
      </div>
    );
  };

  /**
   * Reads one package description into structure.
   *
   * The server parses deterministically first and only consults a model for
   * what it cannot decompose, so most options never cost an API call. Whatever
   * comes back has already been recomputed against the unit engine there; here
   * it is displayed, not applied. Nothing is written until the user accepts it.
   */
  const handleAnalyzePackage = async (option) => {
    if (analysisFor === option.id) { setAnalysisFor(null); return; }
    setAnalysisFor(option.id);
    if (analysisByOptionId[option.id]) return;

    setAnalyzing(option.id);
    try {
      const result = await interpretPurchasePackage({ storeId, purchaseOptionId: option.id });
      setAnalysisByOptionId((prev) => ({ ...prev, [option.id]: result }));
    } finally {
      setAnalyzing(null);
    }
  };

  /**
   * Stores an interpretation the user has accepted.
   *
   * The server revalidates it even though it produced it moments ago — a value
   * that has been through the browser is a proposal again — and writes it where
   * the parser short-circuits on it, so a confirmed package never changes
   * because a parser improved.
   */
  const handleAcceptPackage = async (option) => {
    const analysis = analysisByOptionId[option.id];
    if (!analysis?.interpretation) return;
    setAnalyzing(option.id);
    try {
      const saved = await confirmPurchasePackage({
        storeId,
        purchaseOptionId: option.id,
        interpretation: analysis.interpretation,
      });
      if (!saved.ok) {
        setAnalysisByOptionId((prev) => ({
          ...prev,
          [option.id]: { ...analysis, message: saved.error, rejected: true },
        }));
        return;
      }
      setAnalysisFor(null);
      setAnalysisByOptionId((prev) => {
        const next = { ...prev };
        delete next[option.id];
        return next;
      });
      await load();
      onChanged?.();
    } finally {
      setAnalyzing(null);
    }
  };

  const handleReanalyze = (option) => {
    setAnalysisByOptionId((prev) => {
      const next = { ...prev };
      delete next[option.id];
      return next;
    });
    handleAnalyzePackage({ ...option, id: option.id });
  };

  /**
   * The interpretation, laid out as the facts it is made of rather than as a
   * paragraph. A buyer accepting "24 bottles of 6 fl oz = 144 fl oz" can check
   * that against the box in front of them; a sentence saying "this is a case"
   * cannot be checked at all.
   */
  const renderAnalysis = (option) => {
    const analysis = analysisByOptionId[option.id];
    if (analyzing === option.id && !analysis) {
      return <div style={styles.muted} data-testid="package-analysis">Reading the package description…</div>;
    }
    if (!analysis) return <div style={styles.muted} data-testid="package-analysis">No analysis yet.</div>;

    const interp = analysis.interpretation;
    const total = interp?.quantity_in_item_count_unit;
    const resolved = total != null;

    return (
      <div style={styles.analysisPanel} data-testid="package-analysis">
        {!resolved ? (
          <div style={styles.analysisWarn}>
            <strong>Not resolved.</strong>{' '}
            {analysis.message
              || 'This package size could not be worked out. Enter qty per package by hand and it will be used as-is.'}
          </div>
        ) : (
          <>
            <div style={styles.analysisGrid}>
              <span style={styles.analysisLabel}>Order unit</span>
              <span>{interp.order_unit || '—'}</span>

              <span style={styles.analysisLabel}>Inner packs</span>
              <span>
                {interp.inner_pack_count != null
                  ? `${interp.inner_pack_count} × ${interp.quantity_per_inner_pack} ${interp.quantity_unit || ''}`
                  : 'none — a single container'}
              </span>

              <span style={styles.analysisLabel}>Whole package</span>
              <span>
                {interp.total_package_quantity} {interp.total_package_unit || ''}
              </span>

              <span style={styles.analysisLabel}>In {countUnit || 'count unit'}</span>
              <span data-testid="package-analysis-total"><strong>{total}</strong></span>

              <span style={styles.analysisLabel}>Source</span>
              <span>
                {analysis.usedAi ? 'AI reading of the description' : 'Parsed from the pack description'}
                {interp.confidence != null ? ` · confidence ${Math.round(interp.confidence * 100)}%` : ''}
              </span>
            </div>

            {(interp.assumptions?.length > 0 || interp.warnings?.length > 0) && (
              <div style={styles.analysisWarn}>
                {(interp.assumptions || []).map((note) => <div key={note}>Assumed: {note}</div>)}
                {(interp.warnings || []).map((note) => <div key={note}>{note}</div>)}
              </div>
            )}
          </>
        )}

        <div style={{ ...styles.actions, marginTop: 8 }}>
          {resolved && (
            <button
              type="button"
              style={styles.smallBtn}
              disabled={analyzing === option.id}
              data-testid="package-analysis-accept"
              onClick={() => handleAcceptPackage(option)}
            >
              {analyzing === option.id ? 'Saving…' : 'Accept'}
            </button>
          )}
          <button
            type="button"
            style={styles.smallBtn}
            disabled={analyzing === option.id}
            onClick={() => handleReanalyze(option)}
          >
            Re-analyze
          </button>
          {/* Leaving it unresolved is a real choice, not a failure to decide.
              An unconfirmed package is excluded from counts and flagged, which
              is safer than a confirmed wrong number. */}
          <button type="button" style={styles.smallBtn} onClick={() => setAnalysisFor(null)}>
            Leave unresolved
          </button>
        </div>
      </div>
    );
  };

  const renderActions = (option, inactive) => {
    const conversionUnresolved = option.conversion_status === 'unresolved';
    const menuOpen = actionsFor === option.id;
    const runFromMenu = (action) => {
      setActionsFor(null);
      action();
    };
    return (
      <div style={styles.actions}>
        <button
          type="button"
          style={styles.moreBtn}
          disabled={busy}
          aria-label={`More actions for ${option.vendor_description || resolveVendorName(option) || 'purchase option'}`}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          title="More actions"
          onClick={() => setActionsFor(menuOpen ? null : option.id)}
        >
          …
        </button>
        {menuOpen && (
          <div style={styles.actionMenu} role="menu" data-testid="purchase-option-actions-menu">
            {!inactive && !option.is_preferred && (
              <button
                type="button"
                role="menuitem"
                style={styles.actionMenuBtn}
                disabled={busy}
                onClick={() => runFromMenu(() => handleMakePreferred(option))}
              >
                Make preferred
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              style={styles.actionMenuBtn}
              onClick={() => runFromMenu(() => toggleHistory(option))}
            >
              {historyFor === option.id ? 'Hide history' : 'Price history'}
            </button>
            {!inactive && (
              <button
                type="button"
                role="menuitem"
                data-testid="purchase-option-ordering-rules"
                style={styles.actionMenuBtn}
                disabled={busy}
                title="Vendor minimum, order increment, fractional ordering"
                onClick={() => runFromMenu(() => setOrderingRulesFor(option))}
              >
                Ordering rules…
              </button>
            )}
            {conversionUnresolved && (
              <button
                type="button"
                role="menuitem"
                style={{ ...styles.actionMenuBtn, ...styles.amberBtn, width: '100%' }}
                onClick={() => runFromMenu(() => setConversionFor(conversionFor === option.id ? null : option.id))}
              >
                Review conversion
              </button>
            )}
            {/* Reading a pack description into structure. Deterministic parsing
                handles most descriptions; analysis remains available without
                occupying permanent space in every row. */}
            {!inactive && (
              <button
                type="button"
                role="menuitem"
                style={styles.actionMenuBtn}
                disabled={busy || analyzing === option.id}
                title="Work out how much one of this package holds"
                onClick={() => runFromMenu(() => handleAnalyzePackage(option))}
              >
                {analyzing === option.id
                  ? 'Analyzing…'
                  : analysisFor === option.id ? 'Hide analysis' : 'Analyze package'}
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              style={styles.actionMenuBtn}
              disabled={busy}
              title="Group this option with the item it really belongs to"
              onClick={() => runFromMenu(() => openMovePanel(option))}
            >
              {moveFor === option.id ? 'Cancel move' : 'Move…'}
            </button>
            {inactive && (
              <button
                type="button"
                role="menuitem"
                style={styles.actionMenuBtn}
                disabled={busy}
                onClick={() => runFromMenu(() => handleReactivate(option))}
              >
                Reactivate
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderRow = (option) => {
    const inactive = option.status !== 'active';
    const unitCost = Number.parseFloat(option.normalized_unit_cost);
    const comparableRow = comparableRowById.get(String(option.id)) ?? null;
    const confidencePct = comparableRow?.estimated && Number.isFinite(Number(comparableRow.confidence))
      ? Math.round(Number(comparableRow.confidence) * 100)
      : null;
    const ageDays = priceAgeDays(option);
    const vendorName = option.vendor_id != null ? vendorNameById[option.vendor_id] : null;
    // Fallback when the option never linked to a vendor (resolution failed at
    // import): the raw vendor name as it appeared in the source document.
    const rawVendorText = option.vendor_id == null ? (option.vendor_name_text || '').trim() : '';

    return (
      <React.Fragment key={option.id}>
        <tr style={styles.row(inactive)} data-testid="purchase-option-row">
          <td style={styles.td}>
            {option.vendor_id == null ? (
              rawVendorText ? (
                <span style={styles.vendor} data-testid="vendor-name-text">
                  {rawVendorText}
                  <span style={styles.unlinkedTag} title="Vendor name from the source document — not linked to a vendor yet">unlinked</span>
                </span>
              ) : (
                <span style={styles.noVendor} title="No vendor" data-testid="no-vendor">—</span>
              )
            ) : vendorName ? (
              <span style={styles.vendor}>{vendorName}</span>
            ) : (
              <span style={styles.noVendor}>Vendor …</span>
            )}
            {option.is_preferred && <span style={styles.star} title="Preferred way to buy">★</span>}
          </td>
          <td style={{ ...styles.td, ...styles.muted }}>{option.vendor_description || '—'}</td>
          <td style={styles.td}>{option.vendor_sku || '—'}</td>
          <td style={styles.td}>
            {option.pack_description || option.purchase_unit || '—'}
            {option.moq_packages ? <span style={styles.sub}>MOQ {option.moq_packages} pkg</span> : null}
            {option.source_type ? <span style={styles.sub}>source: {option.source_type}</span> : null}
          </td>
          <td style={styles.td}>
            {option.qty_per_package ?? '—'} {countUnit || ''}
          </td>
          <td style={styles.td}>{money(option.package_price)}</td>
          <td style={styles.td}>
            {Number.isFinite(unitCost) ? `${money(unitCost)}/${countUnit || 'unit'}` : '—'}
          </td>
          <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
            {/* One relationship unit for every row. An option the comparison
                could not resolve gets words, never an invented number. */}
            {comparableRow?.comparableDisplay ? (
              <span data-testid="comparable-price" title={comparableRow.assumption || undefined}>
                {comparableRow.comparableDisplay}
                {comparableRow.isLowest && <span style={styles.lowestTag}>lowest</span>}
                {comparableRow.isTiedLowest && <span style={styles.lowestTag}>tied lowest</span>}
                <span style={styles.sub}>
                  {comparableRow.statusLabel}
                  {confidencePct != null ? ` · ${confidencePct}%` : ''}
                </span>
              </span>
            ) : comparableRow && comparable.comparisonAvailable ? (
              <span style={styles.sub} data-testid="comparable-needs-review">Needs review</span>
            ) : '—'}
          </td>
          <td style={styles.td} title={formatDate(option.effective_date ?? option.updated_at)}>
            {ageDays === null ? '—' : (
              ageDays > STALE_PRICE_DAYS ? (
                <span style={styles.ageStale} data-testid="age-stale">{ageDays}d</span>
              ) : `${ageDays}d`
            )}
          </td>
          <td style={styles.td}>{renderBadges(option, inactive)}</td>
          <td style={styles.td}>{renderActions(option, inactive)}</td>
        </tr>

        {analysisFor === option.id && (
          <tr>
            <td colSpan={COLUMN_COUNT} style={{ ...styles.td, background: '#FFFDF7' }}>
              {renderAnalysis(option)}
            </td>
          </tr>
        )}

        {conversionFor === option.id && (
          <tr>
            <td colSpan={COLUMN_COUNT} style={{ ...styles.td, background: '#FFFDF7' }}>
              <div style={styles.snapshot} data-testid="conversion-snapshot">
                {option.conversion_snapshot
                  ? JSON.stringify(option.conversion_snapshot, null, 2)
                  : 'No conversion details recorded.'}
              </div>
            </td>
          </tr>
        )}

        {moveFor === option.id && (
          <tr>
            <td colSpan={COLUMN_COUNT} style={{ ...styles.td, background: '#FFFDF7' }}>
              <div style={styles.movePanel} data-testid="move-panel">
                <div style={styles.field}>
                  <label style={styles.fieldLabel} htmlFor={`move-search-${option.id}`}>Move to item</label>
                  <input
                    id={`move-search-${option.id}`}
                    type="search"
                    style={{ ...styles.input, width: '100%' }}
                    placeholder="Search items by name…"
                    value={moveQuery}
                    onChange={(e) => {
                      setMoveQuery(e.target.value);
                      searchMoveTargets(e.target.value);
                    }}
                  />
                </div>
                {moveSearching && <div style={{ ...styles.muted, marginTop: 4 }}>Searching…</div>}
                {!moveSearching && moveQuery.trim() !== '' && moveResults.length === 0 && (
                  <div style={{ ...styles.muted, marginTop: 4 }}>No matching items.</div>
                )}
                {moveResults.map((target) => (
                  <button
                    key={target.id}
                    type="button"
                    style={styles.moveTargetBtn}
                    data-testid="move-target"
                    disabled={busy}
                    onClick={() => handleMoveConfirm(option, target)}
                  >
                    {target.description || `Item ${target.id}`}
                    {[target.brand, target.vendor].filter(Boolean).length > 0 &&
                      ` (${[target.brand, target.vendor].filter(Boolean).join(' · ')})`}
                  </button>
                ))}
                <div style={{ marginTop: 8 }}>
                  <button type="button" style={styles.smallBtn} onClick={closeMovePanel}>
                    Cancel
                  </button>
                </div>
              </div>
            </td>
          </tr>
        )}

        {historyFor === option.id && (
          <tr>
            <td colSpan={COLUMN_COUNT} style={{ ...styles.td, background: '#FDFBF9' }}>
              <div style={styles.historyBox}>
                {historyLoading ? (
                  <div style={styles.muted}>Loading price history…</div>
                ) : visibleHistoryRows.length === 0 ? (
                  <div style={styles.muted}>No price changes recorded for this option.</div>
                ) : (
                  visibleHistoryRows.map((row, idx) => (
                    <div key={row.id || idx} style={styles.historyRow}>
                      <strong>{formatDate(row.changed_at)}</strong>
                      {' · '}{row.source || 'unknown'}
                      {' · case '}{money(row.old_case_price)}{' → '}{money(row.new_case_price)}
                      {' · unit '}{money(row.old_unit_price)}{' → '}{money(row.new_unit_price)}
                      {row.normalized_unit_cost != null && ` · ${money(row.normalized_unit_cost)}/${countUnit || 'unit'}`}
                    </div>
                  ))
                )}
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div style={styles.wrap}>
      {orderingRulesFor && (
        <OrderingRulesEditor
          option={orderingRulesFor}
          onClose={() => setOrderingRulesFor(null)}
          onSaved={async () => {
            await load();
            if (typeof onChanged === 'function') onChanged();
          }}
        />
      )}
      {showSummary && (
        <div style={{ marginBottom: 14 }}>
          <ItemPurchaseSummary summary={costSummary} headline={false} />
        </div>
      )}
      <div style={styles.summary}>{optionSummaryLine(options)}</div>
      {error && <div style={styles.error} role="alert">{error}</div>}

      {options.length === 0 ? (
        <div style={{ ...styles.muted, marginBottom: 10 }}>
          No purchase options yet — add the first way to buy this item below.
        </div>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Vendor</th>
                <th style={styles.th}>Vendor item</th>
                <th style={styles.th}>SKU</th>
                <th style={styles.th}>Pack</th>
                <th style={styles.th}>Qty/pkg</th>
                <th style={styles.th}>Package $</th>
                <th style={styles.th}>Unit cost</th>
                <th style={styles.th}>Comparable</th>
                <th style={styles.th}>Age</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {options.map(renderRow)}
            </tbody>
          </table>
        </div>
      )}

      {/* Manual comparison review normally compares two or more active
          options. A linked-group unresolved row may route one member option
          here because the sibling options live on sibling inventory items. */}
      {(activeOptionRows.length >= 2
        || (allowSingleOptionComparisonReview && activeOptionRows.length >= 1)) && (
        <div style={{ marginBottom: 10 }}>
          <button
            type="button"
            style={styles.smallBtn}
            data-testid="review-comparison-toggle"
            onClick={toggleReviewPanel}
          >
            {reviewOpen ? 'Close comparison review' : 'Review comparison'}
          </button>
          {hasManualOverride && !reviewOpen && (
            <span style={{ ...styles.muted, marginLeft: 8 }}>
              Compared by {comparable.comparisonUnitLabel} — set by manual review.
            </span>
          )}
          {reviewOpen && (
            <div style={{ ...styles.addPanel, marginTop: 8 }} data-testid="review-comparison-panel">
              <strong style={{ fontSize: 13 }}>Review price comparison</strong>
              <div style={{ ...styles.muted, marginTop: 3 }}>
                {lockedReviewUnit
                  ? `This vendor package belongs to a linked group compared in ${lockedReviewUnit}. State how many ${lockedReviewUnit} this package contains; saving marks its quantity verified.`
                  : 'Every vendor package is compared in one practical unit. State how much each package holds in that unit; saving marks the quantities verified and overrides the automatic comparison.'}
              </div>
              <div style={styles.formGrid}>
                <div style={styles.field}>
                  <label style={styles.fieldLabel} htmlFor="rc-unit">Comparison unit</label>
                  <select
                    id="rc-unit"
                    data-testid="review-comparison-unit"
                    style={styles.input}
                    value={reviewUnit}
                    disabled={Boolean(lockedReviewUnit)}
                    onChange={(e) => setReviewUnit(e.target.value)}
                  >
                    {MANUAL_COMPARISON_UNITS.map((unit) => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={styles.formGrid}>
                {activeOptionRows.map((option) => (
                  <div key={option.id} style={styles.field}>
                    <label style={styles.fieldLabel} htmlFor={`rc-qty-${option.id}`}>
                      {[resolveVendorName(option) || 'No vendor',
                        option.vendor_description || option.pack_description || option.purchase_unit,
                      ].filter(Boolean).join(' — ')}
                    </label>
                    <input
                      id={`rc-qty-${option.id}`}
                      data-testid={`review-qty-${option.id}`}
                      style={styles.input}
                      type="number"
                      step="any"
                      min="0"
                      placeholder={`${reviewUnit} per package`}
                      value={reviewQty[option.id] ?? ''}
                      onChange={(e) => {
                        const { value } = e.target;
                        setReviewQty((prev) => ({ ...prev, [option.id]: value }));
                      }}
                    />
                    <span style={styles.sub}>{reviewUnit} per package</span>
                  </div>
                ))}
              </div>
              {reviewError && (
                <div style={{ ...styles.error, marginTop: 8, marginBottom: 0 }} role="alert">{reviewError}</div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  style={styles.primaryBtn}
                  disabled={busy}
                  data-testid="review-comparison-save"
                  onClick={handleReviewSave}
                >
                  {busy ? 'Saving…' : 'Save & mark verified'}
                </button>
                {hasManualOverride && (
                  <button
                    type="button"
                    style={styles.smallBtn}
                    disabled={busy}
                    data-testid="review-comparison-clear"
                    title="Removes the manual override and re-resolves the comparison automatically"
                    onClick={handleReviewClear}
                  >
                    Use automatic comparison
                  </button>
                )}
                <button type="button" style={styles.smallBtn} onClick={() => setReviewOpen(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {!addOpen ? (
        <button type="button" style={styles.primaryBtn} onClick={() => { setAddOpen(true); setFormError(''); }}>
          + Add purchase option
        </button>
      ) : (
        <form style={styles.addPanel} onSubmit={handleAddSubmit}>
          <strong style={{ fontSize: 13 }}>Add a way to buy this item</strong>
          <div style={styles.formGrid}>
            <div style={styles.field}>
              <label style={styles.fieldLabel} htmlFor="po-vendor">Vendor</label>
              <select id="po-vendor" style={styles.input} value={form.vendorId} onChange={setField('vendorId')}>
                <option value="">— No vendor —</option>
                {vendors.map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>{vendor.display_name}</option>
                ))}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.fieldLabel} htmlFor="po-sku">Vendor SKU</label>
              <input id="po-sku" style={styles.input} type="text" placeholder="e.g. 884512" value={form.vendorSku} onChange={setField('vendorSku')} />
            </div>
            <div style={styles.field}>
              <label style={styles.fieldLabel} htmlFor="po-pack">Pack description</label>
              <input id="po-pack" style={styles.input} type="text" placeholder="e.g. 4 × 1 gal case" value={form.packDescription} onChange={setField('packDescription')} />
            </div>
            <div style={styles.field}>
              <label style={styles.fieldLabel} htmlFor="po-unit">Purchase unit</label>
              <input id="po-unit" style={styles.input} type="text" placeholder="e.g. case" value={form.purchaseUnit} onChange={setField('purchaseUnit')} />
            </div>
            <div style={styles.field}>
              <label style={styles.fieldLabel} htmlFor="po-qty">Qty per package ({countUnit || 'count unit'}) *</label>
              <input id="po-qty" style={styles.input} type="number" step="any" min="0" placeholder="e.g. 4" value={form.qtyPerPackage} onChange={setField('qtyPerPackage')} />
            </div>
            <div style={styles.field}>
              <label style={styles.fieldLabel} htmlFor="po-price">Package price *</label>
              <input id="po-price" style={styles.input} type="number" step="0.01" min="0" placeholder="e.g. 18.40" value={form.packagePrice} onChange={setField('packagePrice')} />
            </div>
            <div style={styles.field}>
              <label style={styles.fieldLabel} htmlFor="po-moq">MOQ (packages)</label>
              <input id="po-moq" style={styles.input} type="number" step="1" min="0" placeholder="1 (default)" value={form.moqPackages} onChange={setField('moqPackages')} />
            </div>
            <div style={styles.field}>
              <label style={styles.fieldLabel} htmlFor="po-increment">Order increment (packages)</label>
              <input id="po-increment" style={styles.input} type="number" step="any" min="0" placeholder="blank = whole packages" value={form.orderIncrement} onChange={setField('orderIncrement')} title="The steps this vendor sells in. 0.25 on a by-the-pound option means quarter-pound steps; blank means whole packages." />
            </div>
          </div>
          {formError && <div style={{ ...styles.error, marginTop: 8, marginBottom: 0 }} role="alert">{formError}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="submit" style={styles.primaryBtn} disabled={busy}>
              {busy ? 'Saving…' : 'Save option'}
            </button>
            <button type="button" style={styles.smallBtn} onClick={() => { setAddOpen(false); setForm(EMPTY_FORM); setFormError(''); }}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
